let translations = {};
let currentLang = 'english';
let defaultLang = 'english';
let availableLanguages = [];
let languagesFetched = false;
let languagesFetchPromise = null;

const listeners = [];

export const subscribe = (listener) => {
  listeners.push(listener);
  return () => {
    const index = listeners.indexOf(listener);
    if (index > -1) {
      listeners.splice(index, 1);
    }
  };
};

const notifyListeners = () => {
  listeners.forEach(listener => listener());
};

export const fetchAvailableLanguages = async () => {
  // Return cached result immediately
  if (languagesFetched) return availableLanguages;

  // Deduplicate in-flight requests — return the same promise if already fetching
  if (languagesFetchPromise) return languagesFetchPromise;

  languagesFetchPromise = (async () => {
    try {
      const baseURL = process.env.REACT_APP_BASE_URL || '';
      const response = await fetch(`${baseURL}/language/list`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();

      // Handle the new structure: data.data.languages and data.data.default
      availableLanguages = data.data.languages || [];
      defaultLang = data.data.default?.language_name;

      languagesFetched = true;
      return availableLanguages;
    } catch (err) {
      console.error('Failed to fetch languages:', err);
      availableLanguages = [{ language_name: 'english' }, { language_name: 'hindi' }];
      languagesFetched = true;
      return availableLanguages;
    } finally {
      languagesFetchPromise = null;
    }
  })();

  return languagesFetchPromise;
};

export const initLanguage = async () => {
  try {
    const storedLang = localStorage.getItem('lang');
    const storedData = localStorage.getItem('translations');

    if (storedLang && storedData) {
      currentLang = storedLang;
      translations = JSON.parse(storedData);
      return;
    }
  } catch {
    localStorage.clear();
  }

  // If no stored language, fetch the list to find the dynamic default
  try {
    await fetchAvailableLanguages();
    await loadLanguage(defaultLang);
  } catch (error) {
    console.error("Initialization language failed, falling back to english", error);
    await loadLanguage('english');
  }
};

export const loadLanguage = async (lng) => {
  if (lng === currentLang && Object.keys(translations).length > 0) {
    return;
  }

  try {
    const baseURL = process.env.REACT_APP_BASE_URL || '';

    const response = await fetch(`${baseURL}/language/translations?locale=${lng}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    const translationData = data.data || data;

    currentLang = lng;
    translations = translationData;

    localStorage.setItem('lang', lng);
    localStorage.setItem('translations', JSON.stringify(translationData));

    notifyListeners();
  } catch (err) {
    console.error('Translation load failed', err);
    if (lng !== 'english') {
      await loadLanguage('english');
    }
  }
};

export const t = (key) => {
  const translation = translations[key] || key.replaceAll(/_/g, ' ').replaceAll(/\b\w/g, char => char.toUpperCase());
  return translation;
};

export const getLang = () => currentLang;